import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { toEntityLinkVerdict } from "~/ai-chat/data-source/use-entity-link-verdict";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import {
  collectConversationArtifacts,
  type SavedArtifactRef,
  type VerifiedConversationArtifact,
} from "~/ai-chat/model/conversation-artifacts";
import { resourceExistsQueryOptions } from "~/resource/data-source/api/resource-exists-query-options";

// The Artifacts panel lists the assistant's saved links and its unsaved drafts. Links can
// name resources that don't exist, so confirmed misses are dropped; pending and unverifiable
// ones stay listed with their verdict so a slow or failed check never empties the panel. The
// lookups share cache entries with the transcript chips. Drafts need no lookup, and resources
// saved from this chat were just created, so they skip it too — the list endpoint can lag
// the write and would misreport them.
export function useConversationArtifacts(
  messages: ChatTranscriptItem[],
  savedArtifacts: SavedArtifactRef[],
): VerifiedConversationArtifact[] {
  // Stable references for useQueries (ref-sensitive: it subscribes per array entry).
  const artifacts = useMemo(() => collectConversationArtifacts(messages, savedArtifacts), [messages, savedArtifacts]);
  const savedIds = useMemo(() => new Set(savedArtifacts.map((artifact) => artifact.resourceId)), [savedArtifacts]);
  return useQueries({
    queries: artifacts.map((artifact) => ({
      ...resourceExistsQueryOptions(artifact.kind === "saved" ? artifact.resourceId : ""),
      enabled: artifact.kind === "saved" && !savedIds.has(artifact.resourceId),
    })),
    combine: (results): VerifiedConversationArtifact[] =>
      artifacts.flatMap<VerifiedConversationArtifact>((artifact, index) => {
        if (artifact.kind === "draft") return [{ ...artifact, verdict: "found" as const }];
        const verdict = savedIds.has(artifact.resourceId) ? "found" : toEntityLinkVerdict(results[index], false);
        return verdict === "missing" ? [] : [{ ...artifact, verdict }];
      }),
  });
}
